// src/services/qualityInspectionApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

// Types
export interface QualityInspectionConfiguration {
  canInspect: string[]
  canReview: string[]
  finalApprover: string
  allowSelfReview: boolean
  requireClientSignoff: boolean
  requirePhotos: boolean
  requireSignature: boolean
  useDetailedInspections: boolean
}

export interface QualityTeamMember {
  employeeId: string
  userId: string
  name: string
  email: string
  user: {
    _id: string
    name: string
    surname: string
    email: string
    fullName: string
  } | null
  qualityRole: string
  qualityAssignedDate: string
  qualityPermissions: any
  mainRole: string
  isActive: boolean
}

export interface AssignQualityRoleRequest {
  businessId: string
  userId: string
  role: string
}

export interface UpdateQualityConfigRequest {
  businessId: string
  canInspect: string[]
  canReview: string[]
  finalApprover: string
  allowSelfReview: boolean
  requireClientSignoff: boolean
  requirePhotos: boolean
  requireSignature: boolean
  useDetailedInspections: boolean
}

export const qualityInspectionApi = createApi({
  reducerPath: 'qualityInspectionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/quality-inspections`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['QualityConfig', 'QualityTeam'],
  endpoints: (builder) => ({
    // ============================================================================
    // CONFIGURATION ENDPOINTS
    // ============================================================================
    
    // Get quality inspection configuration
    getQualityInspectionConfig: builder.query<QualityInspectionConfiguration, string>({
      query: (businessId) => ({
        url: '/config',
        params: { businessId }
      }),
      transformResponse: (response: any) => response.data?.config || response,
      providesTags: ['QualityConfig']
    }),
    
    // Update quality inspection configuration
    updateQualityInspectionConfig: builder.mutation<any, UpdateQualityConfigRequest>({
      query: ({ businessId, ...configData }) => ({
        url: '/config',
        method: 'PUT',
        params: { businessId },
        body: configData
      }),
      invalidatesTags: ['QualityConfig']
    }),

    // ============================================================================
    // TEAM MANAGEMENT ENDPOINTS
    // ============================================================================
    
    // Get quality team
    getQualityTeam: builder.query<QualityTeamMember[], string>({
      query: (businessId) => ({
        url: '/team',
        params: { businessId }
      }),
      providesTags: ['QualityTeam'],
      transformResponse: (response: any) => response.data?.qualityTeam || []
    }),
    
    // Assign quality role
    assignQualityRole: builder.mutation<any, AssignQualityRoleRequest>({
      query: ({ businessId, ...assignData }) => ({
        url: '/team/assign',
        method: 'POST',
        params: { businessId },
        body: assignData
      }),
      invalidatesTags: ['QualityTeam']
    }),
    
    // Remove quality role
    removeQualityRole: builder.mutation<any, { businessId: string; userId: string }>({
      query: ({ businessId, userId }) => ({
        url: `/team/${userId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['QualityTeam']
    }),

    // ============================================================================
    // ANALYTICS AND INSIGHTS
    // ============================================================================
    
    // Get quality insights and statistics
    getQualityInsights: builder.query<any, string>({
      query: (businessId) => ({
        url: '/insights',
        params: { businessId }
      }),
      providesTags: ['QualityConfig']
    }),
    
    // Get available roles for assignment
    getAvailableRoles: builder.query<string[], void>({
      query: () => ({
        url: '/available-roles'
      })
    }),
    
    // Get employees eligible for quality roles
    getEligibleEmployees: builder.query<any[], string>({
      query: (businessId) => ({
        url: '/eligible-employees',
        params: { businessId }
      }),
      providesTags: ['QualityTeam']
    })
  })
});

export const {
  // Configuration hooks
  useGetQualityInspectionConfigQuery,
  useUpdateQualityInspectionConfigMutation,
  
  // Team management hooks
  useGetQualityTeamQuery,
  useAssignQualityRoleMutation,
  useRemoveQualityRoleMutation,
  
  // Additional utility hooks
  useGetQualityInsightsQuery,
  useGetAvailableRolesQuery,
  useGetEligibleEmployeesQuery
} = qualityInspectionApi;